import { useEffect, useState } from 'react'
import { getLeaderboard, getLeaderboardLive } from '../../services/api'
import { useT } from '../../hooks/useTranslation'

// Формат денежной суммы: $360 → «$360», $1234.5 → «$1,235», $12.34 → «$12.34».
const fmtUsd = (x) => {
  const n = Number(x) || 0
  if (n >= 1000) return '$' + n.toLocaleString('en-US', { maximumFractionDigits: 0 })
  return '$' + (Number.isInteger(n) ? String(n) : n.toFixed(2))
}

// Депозит участника (п.25 task.txt) показываем ВСЕГДА с копейками. У
// виртуальных юзеров это стартовый депозит + накопленная прибыль — почти
// всегда дробное число, и «$1,234.56» (а не «$1,235») читается как живой
// баланс реального трейдера.
const fmtDeposit = (x) => {
  const n = Number(x) || 0
  return '$' + n.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
}

// Формат суммы СО ЗНАКОМ: +$45 / -$12.34 (п.2/6 task.txt) — без «+$-» и «++$».
const fmtSigned = (x) => {
  const n = Number(x) || 0
  if (n === 0) return fmtUsd(0)
  return (n < 0 ? '-' : '+') + fmtUsd(Math.abs(n))
}

// Плавное появление новой строки ленты (без внешних CSS-фреймворков — один
// keyframe в <style>, как у остальных анимаций Mini App).
const LIVE_FADE = `
@keyframes scalpexLiveIn {
  from { opacity: 0; transform: translateY(-3px); }
  to   { opacity: 1; transform: translateY(0); }
}
`

// FOMO-блок над доской (п.11, п.23-25 task.txt). Источник — виртуальная
// торговля виртуальных юзеров лидерборда, привязанная к реальным фандингам
// (leaderboardSim.js), ПЛЮС реальные сделки живых юзеров (батч E, п.9:
// решение владельца — цифра «заработали сегодня» и лента считают и реальные
// сделки, отбор тот же, что у доски; см. getLeaderboardLive в db.js).
// Цифра «заработали сегодня» + живая лента последних сделок с ротацией имени
// каждые ~3с. П.2 task.txt: когда сегодня плюсовых
// PNL нет — блок переключается на «заработали вчера» (цифра и лента — по
// вчерашним сделкам). Ненавязчиво: блок компактный, лента серым мелким
// шрифтом.
// Экспортируется (не только для внутреннего использования): блок проверяется
// отдельно от всей страницы — у него своя логика «сегодня/вчера» и своя цифра.
export function FomoBlock() {
  const t = useT()
  const [live, setLive] = useState(null)
  const [idx, setIdx] = useState(0)

  useEffect(() => {
    let alive = true
    const load = async () => {
      try {
        const data = await getLeaderboardLive()
        if (alive) setLive(data)
      } catch { /* API недоступен — блок остаётся без ленты */ }
    }
    load()
    const poll = setInterval(load, 60_000)
    const rot = setInterval(() => setIdx(i => i + 1), 3000)
    return () => { alive = false; clearInterval(poll); clearInterval(rot) }
  }, [])

  // П.2 task.txt: когда сегодня плюсовых PNL нет — показываем «вчера» (цифра
  // yesterday.pnl + лента по вчерашним сделкам recentYesterday, как «сегодня»
  // по recent). Если и вчера данных нет — остаётся прежнее поведение (цифра 0).
  const todayPnl = Number(live?.today?.pnl ?? 0)
  const useToday = todayPnl > 0
  const list = useToday
    ? (Array.isArray(live?.recent) ? live.recent : [])
    : (Array.isArray(live?.recentYesterday) ? live.recentYesterday : [])
  const recent = list.filter(r => r && r.trader)
  const item = recent.length ? recent[idx % recent.length] : null
  const amount = useToday ? todayPnl : Number(live?.yesterday?.pnl ?? 0)

  return (
    <>
      <style>{LIVE_FADE}</style>
      <div style={{
        background: 'linear-gradient(135deg, rgba(255,69,58,0.10) 0%, rgba(255,149,0,0.08) 100%)',
        border: '1px solid rgba(255,69,58,0.16)',
        borderRadius: 14, padding: '10px 14px', marginBottom: 12,
      }}>
        <div style={{ fontSize: 13, fontWeight: 600, color: '#FF8A65', display: 'flex', alignItems: 'baseline' }}>
          <span>{useToday ? t.fomoTitle : t.fomoYesterdayTitle}</span>
          {live ? (
            // П.2 task.txt: сами цифры — зелёные (#34D399 = --accent-soft/--positive),
            // тем же цветом показан плюсовой PnL в ленте ниже. Заголовок блока
            // остаётся оранжевым.
            <span style={{ marginLeft: 6, fontSize: 15, fontWeight: 700, color: '#34D399' }}>
              {fmtUsd(amount)}
            </span>
          ) : null}
        </div>
        {item ? (
          <div
            key={`${item.symbol}-${item.ts}`}
            style={{ fontSize: 11, color: '#8E8E93', marginTop: 5, animation: 'scalpexLiveIn 0.5s ease' }}
          >
            <span style={{ color: '#B0B0B4' }}>{item.trader}</span> · {item.symbol} ·{' '}
            <span style={{ color: item.pnl < 0 ? '#FF3B30' : '#34D399', fontWeight: 600 }}>
              {t.fomoRecentEarned.replace('{x}', fmtSigned(item.pnl))}
            </span>
          </div>
        ) : null}
      </div>
    </>
  )
}

// Демо-данные лидерборда (50 трейдеров) — фолбэк, пока не пришли живые данные.
// Ники — ПСЕВДОНИМЫ того же вида, что отдаёт сервер (см. TG-BOT/src/nickMask.js).
// Раньше здесь лежали реальные Telegram-юзернеймы: они уезжали в бандл и были
// доступны любому, кто открыл приложение. Искать таких людей больше не по чему.
// v3.1: totalPnl — плавная линия ~360 → ~30, ВСЕ и итоги положительные;
// торговые дни коррелируют с прибылью (лидер 45 дн. → хвост 8 дн.).
const LEADERBOARD_DEMO = [
  { rank: 1, trader: 'byte12pe', totalPnl: 360.00, roiWeek: 7.2, days: 45, badge: '👑' },
  { rank: 2, trader: 'zen_hz9a', totalPnl: 353.27, roiWeek: 7.1, days: 45, badge: '🥈' },
  { rank: 3, trader: 'delta_atle', totalPnl: 346.53, roiWeek: 6.9, days: 44, badge: '🥉' },
  { rank: 4, trader: 'solo_1wl4', totalPnl: 339.80, roiWeek: 6.8, days: 43, badge: '' },
  { rank: 5, trader: 'helix_1ebr', totalPnl: 333.06, roiWeek: 6.7, days: 42, badge: '' },
  { rank: 6, trader: 'kilo_lzxz', totalPnl: 326.33, roiWeek: 10.9, days: 42, badge: '' },
  { rank: 7, trader: 'tideaoxg', totalPnl: 319.59, roiWeek: 10.7, days: 41, badge: '' },
  { rank: 8, trader: 'mesabab7', totalPnl: 312.86, roiWeek: 10.4, days: 40, badge: '' },
  { rank: 9, trader: 'sigmak0i9', totalPnl: 306.12, roiWeek: 10.2, days: 39, badge: '' },
  { rank: 10, trader: 'xeno_1xu6', totalPnl: 299.39, roiWeek: 10.0, days: 39, badge: '' },
  { rank: 11, trader: 'zenoajn', totalPnl: 292.65, roiWeek: 5.9, days: 38, badge: '' },
  { rank: 12, trader: 'ultra_1tah', totalPnl: 285.92, roiWeek: 5.7, days: 37, badge: '' },
  { rank: 13, trader: 'ace_1wxw', totalPnl: 279.18, roiWeek: 5.6, days: 36, badge: '' },
  { rank: 14, trader: 'ace_ver9', totalPnl: 272.45, roiWeek: 5.4, days: 36, badge: '' },
  { rank: 15, trader: 'arc_17ov', totalPnl: 265.71, roiWeek: 5.3, days: 35, badge: '' },
  { rank: 16, trader: 'jet_1ool', totalPnl: 258.98, roiWeek: 51.8, days: 34, badge: '' },
  { rank: 17, trader: 'luna1a80', totalPnl: 252.24, roiWeek: 50.4, days: 33, badge: '' },
  { rank: 18, trader: 'arc1qfx', totalPnl: 245.51, roiWeek: 49.1, days: 33, badge: '' },
  { rank: 19, trader: 'gamma1w7l', totalPnl: 238.78, roiWeek: 47.8, days: 32, badge: '' },
  { rank: 20, trader: 'byte_5h7b', totalPnl: 232.04, roiWeek: 46.4, days: 31, badge: '' },
  { rank: 21, trader: 'solo168d', totalPnl: 225.31, roiWeek: 45.1, days: 30, badge: '' },
  { rank: 22, trader: 'gamma_1n3b', totalPnl: 218.57, roiWeek: 43.7, days: 30, badge: '' },
  { rank: 23, trader: 'sigma120c', totalPnl: 211.84, roiWeek: 42.4, days: 29, badge: '' },
  { rank: 24, trader: 'sigma_1maj', totalPnl: 205.10, roiWeek: 41.0, days: 28, badge: '' },
  { rank: 25, trader: 'kilo_1nz3', totalPnl: 198.37, roiWeek: 39.7, days: 27, badge: '' },
  { rank: 26, trader: 'helix_1go3', totalPnl: 191.63, roiWeek: 38.3, days: 27, badge: '' },
  { rank: 27, trader: 'gamma1x9o', totalPnl: 184.90, roiWeek: 37.0, days: 26, badge: '' },
  { rank: 28, trader: 'novah8hl', totalPnl: 178.16, roiWeek: 35.6, days: 25, badge: '' },
  { rank: 29, trader: 'arc_y1h8', totalPnl: 171.43, roiWeek: 34.3, days: 24, badge: '' },
  { rank: 30, trader: 'luna1urs', totalPnl: 164.69, roiWeek: 32.9, days: 24, badge: '' },
  { rank: 31, trader: 'pulse_pmpq', totalPnl: 157.96, roiWeek: 31.6, days: 23, badge: '' },
  { rank: 32, trader: 'pulse1n0g', totalPnl: 151.22, roiWeek: 30.2, days: 22, badge: '' },
  { rank: 33, trader: 'wave1g4w', totalPnl: 144.49, roiWeek: 28.9, days: 21, badge: '' },
  { rank: 34, trader: 'solo_14wg', totalPnl: 137.76, roiWeek: 27.6, days: 21, badge: '' },
  { rank: 35, trader: 'sigma_7mps', totalPnl: 131.02, roiWeek: 26.2, days: 20, badge: '' },
  { rank: 36, trader: 'jet_1ei2', totalPnl: 124.29, roiWeek: 24.9, days: 19, badge: '' },
  { rank: 37, trader: 'xeno1uzq', totalPnl: 117.55, roiWeek: 23.5, days: 18, badge: '' },
  { rank: 38, trader: 'jet_4z7g', totalPnl: 110.82, roiWeek: 22.2, days: 18, badge: '' },
  { rank: 39, trader: 'vega1yo9', totalPnl: 104.08, roiWeek: 20.8, days: 17, badge: '' },
  { rank: 40, trader: 'luna_15gr', totalPnl: 97.35, roiWeek: 19.5, days: 16, badge: '' },
  { rank: 41, trader: 'arc_7laj', totalPnl: 90.61, roiWeek: 18.1, days: 15, badge: '' },
  { rank: 42, trader: 'delta1udl', totalPnl: 83.88, roiWeek: 16.8, days: 15, badge: '' },
  { rank: 43, trader: 'sigma_kq35', totalPnl: 77.14, roiWeek: 15.4, days: 14, badge: '' },
  { rank: 44, trader: 'blitz_e9bh', totalPnl: 70.41, roiWeek: 14.1, days: 13, badge: '' },
  { rank: 45, trader: 'delta_r7gp', totalPnl: 63.67, roiWeek: 12.7, days: 12, badge: '' },
  { rank: 46, trader: 'tide1no0', totalPnl: 56.94, roiWeek: 11.4, days: 12, badge: '' },
  { rank: 47, trader: 'solo40z1', totalPnl: 50.20, roiWeek: 10.0, days: 11, badge: '' },
  { rank: 48, trader: 'nova1ufg', totalPnl: 43.47, roiWeek: 8.7, days: 10, badge: '' },
  { rank: 49, trader: 'kiloqe97', totalPnl: 36.73, roiWeek: 7.3, days: 9, badge: '' },
  { rank: 50, trader: 'quartz55wb', totalPnl: 30.00, roiWeek: 6.0, days: 8, badge: '' },
]

// Имена НЕ маскируем на клиенте: сервер уже отдаёт ЧАСТИЧНО замаскированные
// настоящие имена (TG-BOT/src/nickMask.js): «s0mb0dy» → «s0m***y». Видны только
// начало и последний символ — по такому огрызку человека в Telegram не найти.

function LeaderRow({ item, isLast }) {
  const t = useT()
  const pnl = Number(item.totalPnl)
  const pnlColor = pnl >= 0 ? '#34D399' : '#FF3B30'
  // ROI у РЕАЛЬНОЙ строки может быть null: маржа за неделю не записана и
  // среднего roi_margin по сделкам нет (см. getRealLeaderboardRows в db.js —
  // CASE … ELSE NULL). Без защиты `.toFixed(1)` на null бросал исключение и
  // роняло ВЕСЬ экран лидерборда, а не одну ячейку. null → прочерк нейтральным
  // цветом (как в остальном приложении для «нет данных»).
  const hasRoi = Number.isFinite(item.roiWeek)
  const roiColor = !hasRoi ? '#636366' : (item.roiWeek >= 0 ? '#34D399' : '#FF3B30')

  return (
    <div style={{
      display: 'flex', alignItems: 'center', gap: 10,
      padding: '10px 0',
      borderBottom: isLast ? 'none' : '1px solid rgba(255,255,255,0.04)',
    }}>
      {/* Rank + Badge */}
      <div style={{ width: 28, textAlign: 'center', flexShrink: 0 }}>
        <span style={{ fontSize: 13, fontWeight: 700, color: '#636366' }}>
          {item.badge || item.rank}
        </span>
      </div>

      {/* Trader name */}
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 6, minWidth: 0 }}>
          <div style={{ fontSize: 13, fontWeight: 600, color: '#FFF', lineHeight: 1.2, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
            {item.trader}
          </div>
          {/* Своя строка: под замаскированным именем юзер иначе себя на доске не найдёт */}
          {item.isMe ? (
            <span style={{
              flexShrink: 0, fontSize: 9, fontWeight: 700, letterSpacing: '0.5px',
              color: '#34D399', background: 'rgba(52,211,153,0.14)',
              border: '1px solid rgba(52,211,153,0.30)',
              borderRadius: 5, padding: '1px 5px', textTransform: 'uppercase',
            }}>
              {t.leaderboardYou}
            </span>
          ) : null}
        </div>
        <div style={{ fontSize: 10, color: '#636366', marginTop: 2 }}>
          {t.leaderboardDays.replaceAll('{n}', item.days)}
          {/* Депозит участника (п.25 task.txt): чтобы новичку с депозитом $100
              цифры лидерборда не казались недостижимыми. У виртуальных юзеров
              это старт + накопленная прибыль — сервер уже отдаёт дробное число
              ($345.68). Есть только у виртуальных юзеров (у реальных
              deposit = null — строку не рисуем). */}
          {item.deposit != null && item.deposit !== '' ? (
            <span style={{
              display: 'inline-flex', alignItems: 'baseline', gap: 5,
              marginLeft: 8, padding: '2px 8px',
              background: 'rgba(52,211,153,0.10)',
              border: '1px solid rgba(52,211,153,0.22)',
              borderRadius: 7, fontSize: 10, lineHeight: '14px',
            }}>
              <span style={{ color: '#A3A3A8' }}>{t.leaderboardDepositLabel}</span>
              <span style={{ color: '#FFD60A', fontWeight: 800, fontSize: 11 }}>
                {fmtDeposit(item.deposit)}
              </span>
            </span>
          ) : null}
        </div>
      </div>

      {/* PnL */}
      <div style={{ textAlign: 'right', flexShrink: 0 }}>
        <div style={{ fontSize: 13, fontWeight: 700, color: pnlColor }}>
          {Number.isFinite(pnl) ? `$${pnl.toFixed(2)}` : '—'}
        </div>
        {/* Прибыль юзера ЗА 7 ДНЕЙ (v6-week7 — сервер суммирует сделки недели),
            а не разовый PNL одной сделки и не вся история: подпись совпадает с числом */}
        <div style={{ fontSize: 10, color: '#636366' }}>{t.leaderboardTotalPnl}</div>
      </div>

      {/* ROI */}
      <div style={{ textAlign: 'right', flexShrink: 0, minWidth: 50 }}>
        <div style={{ fontSize: 13, fontWeight: 700, color: roiColor }}>
          {hasRoi ? `${item.roiWeek >= 0 ? '+' : ''}${item.roiWeek.toFixed(1)}%` : '—'}
        </div>
        <div style={{ fontSize: 10, color: '#636366' }}>ROI</div>
      </div>
    </div>
  )
}

function badgeFor(i) {
  if (i === 0) return '👑'
  if (i === 1) return '🥈'
  if (i === 2) return '🥉'
  return ''
}

export default function Leaderboard() {
  const t = useT()
  // Начинаем с демо-строк (нет мигания пустоты), при успешном запросе
  // заменяем на живые данные от /api/leaderboard. При ошибке API — остаёмся
  // на текущих строках (демо или последние живые), блок никогда не пуст.
  const [rows, setRows] = useState(LEADERBOARD_DEMO)

  useEffect(() => {
    let alive = true
    let timer

    const load = async () => {
      try {
        const data = await getLeaderboard()
        if (!alive) return
        if (Array.isArray(data) && data.length) {
          // Ранг и медали вычисляем на клиенте — API возвращает массив
          // без rank/badge (ранг = индекс, как в /api/positions).
          setRows(data.map((r, i) => ({
            ...r,
            rank: i + 1,
            badge: badgeFor(i),
          })))
        }
      } catch {
        // API недоступен — оставляем текущие строки
      }
    }

    load()
    // Таблица обновляется редко, а запрос читает БД — опрос раз в минуту
    // (бэкенд дополнительно кэширует строки на 30с, так что реальная нагрузка
    // на БД — 2 запроса в минуту на всех юзеров).
    timer = setInterval(load, 60_000)
    return () => { alive = false; clearInterval(timer) }
  }, [])

  return (
    <div style={{ marginBottom: 16 }}>
      <h3 style={{ fontSize: 15, fontWeight: 600, color: '#8E8E93', marginBottom: 10 }}>
        {t.leaderboardTitle}
      </h3>

      {/* FOMO-блок: «сколько заработали сегодня» + живая лента (п.11, п.23-25) */}
      <FomoBlock />

      <div style={{
        background: '#1A1A1A', borderRadius: 14, padding: '12px 14px',
        maxHeight: 400, overflowY: 'auto',
      }}>
        {rows.map((item, i) => (
          <LeaderRow key={`${item.rank}-${item.trader}`} item={item} isLast={i === rows.length - 1} />
        ))}
      </div>
    </div>
  )
}
